/**
 * BulkFormImport — Phase 1.5+
 *
 * One modal, three steps: pick → preview → publish.
 *
 *   1. Pick    Download the template OR paste/upload a 3-column CSV
 *              (Field Label, Type, Indicator Name).
 *   2. Preview Field cards rendered from the parsed CSV. Any
 *              Indicator Name that doesn't match an existing indicator
 *              is shown with a warning chip — the form still imports,
 *              that row just stays unlinked (option 4a).
 *   3. Publish Choose "Save draft" or "Publish" — both POST to the
 *              existing /programs/{pid}/forms endpoint.
 *
 * Pure FE CSV parsing (no new backend route). Indicator matching is
 * case + whitespace insensitive against indicator NAME (matches Phase
 * 1.5 auto-mapping in FormsTab.jsx). When two CSV rows produce the
 * same field key, we de-dupe with a numeric suffix so the existing
 * form-validation rule (unique keys) doesn't reject the import.
 */
import { useState, useRef } from "react";
import { toast } from "sonner";
import {
  Loader2,
  Upload,
  Download,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  ArrowLeft,
  Wand2,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import api, { formatApiError } from "../../lib/api";

// Mirror /api/forms/field-types — keep in sync if the backend expands.
const TYPE_ALIASES = {
  text: "text",
  short: "text",
  "short text": "text",
  string: "text",
  paragraph: "textarea",
  long: "textarea",
  "long text": "textarea",
  textarea: "textarea",
  number: "text", // numeric data is captured via text field with numeric value-key
  password: "password",
  dropdown: "select",
  select: "select",
  radio: "radio",
  multiple: "checkbox",
  checkbox: "checkbox",
  checkboxes: "checkbox",
  date: "datetime",
  datetime: "datetime",
  time: "datetime",
  location: "location",
  gps: "location",
  slider: "range",
  range: "range",
};

const normalize = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const slug = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || "field";

// Tolerant CSV split that handles quoted commas. We don't need a full
// RFC-4180 parser — three columns, no embedded newlines is enough for
// the supported template.
function parseCsv(text) {
  const rows = [];
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const cells = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
        else inQ = !inQ;
      } else if (ch === "," && !inQ) {
        cells.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
    cells.push(cur);
    rows.push(cells.map((c) => c.trim()));
  }
  return rows;
}

function downloadTemplate() {
  const lines = [
    "Field Label,Type,Indicator Name",
    "Children enrolled,Number,Children enrolled",
    "Photo of enrolment register,Short text,",
    "Date of visit,Date,",
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "array-engine-form-template.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export default function BulkFormImport({ pid, indicators, open, onClose, onImported }) {
  const [step, setStep] = useState("pick"); // pick | preview
  const [formName, setFormName] = useState("");
  const [rows, setRows] = useState([]);     // [{label, type, indicator_name, key, indicator_id, warning}]
  const [csvText, setCsvText] = useState("");
  const [saving, setSaving] = useState(false);
  const [brief, setBrief] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => {
    setStep("pick");
    setFormName("");
    setRows([]);
    setCsvText("");
    setBrief("");
  };
  const close = () => { reset(); onClose(); };

  // Build a normalized lookup so 'Children Enrolled' matches indicator
  // 'children enrolled' / 'Children  enrolled' alike.
  const indicatorByName = new Map();
  for (const i of indicators || []) {
    const n = normalize(i.name);
    if (n) indicatorByName.set(n, i);
  }

  const ingest = (text) => {
    const parsed = parseCsv(text);
    if (parsed.length === 0) {
      toast.error("CSV is empty");
      return;
    }
    // Detect + skip header row when first cell looks like 'Field Label'.
    let body = parsed;
    const first = (parsed[0][0] || "").toLowerCase();
    if (first.includes("field") && first.includes("label")) body = parsed.slice(1);
    if (body.length === 0) {
      toast.error("CSV has a header but no data rows");
      return;
    }

    const seenKeys = new Set();
    const built = [];
    for (const r of body) {
      const label = (r[0] || "").trim();
      const typeRaw = (r[1] || "text").trim();
      const indName = (r[2] || "").trim();
      if (!label) continue;
      const t = TYPE_ALIASES[normalize(typeRaw)] || "text";
      // De-dupe keys with a numeric suffix to satisfy the unique-key rule.
      let key = slug(label);
      let suffix = 2;
      while (seenKeys.has(key)) { key = `${slug(label)}_${suffix++}`; }
      seenKeys.add(key);
      const ind = indName ? indicatorByName.get(normalize(indName)) : null;
      built.push({
        label,
        type: t,
        type_raw: typeRaw,
        indicator_name: indName,
        indicator_id: ind ? ind.id : null,
        indicator_code: ind ? ind.code : null,
        key,
        warning: indName && !ind ? `No indicator matches "${indName}" — this field will stay unlinked.` : null,
      });
    }
    if (built.length === 0) {
      toast.error("No valid rows found in CSV (rows need at least a Field Label).");
      return;
    }

    setRows(built);
    if (!formName) {
      setFormName(`Imported form · ${new Date().toLocaleDateString()}`);
    }
    setStep("preview");
  };

  const onFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      if (!text.trim()) {
        toast.error("CSV is empty");
        return;
      }
      setCsvText(text);
      ingest(text);
    } catch (e) {
      toast.error(`Could not read file: ${e.message}`);
    }
  };

  // AI-suggest: send the brief to Claude (via Emergent LLM key), receive a
  // 3-column CSV, drop it into the paste box AND auto-parse so the user
  // lands directly on the preview step where they can review every field.
  const askAi = async () => {
    if (brief.trim().length < 10) {
      toast.error("Add a bit more detail — a sentence or two about what you're measuring works best.");
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await api.post(`/programs/${pid}/forms/ai-suggest-csv`, {
        brief: brief.trim(),
      });
      const csv = (data && data.csv) || "";
      if (!csv) throw new Error("Empty AI response");
      setCsvText(csv);
      ingest(csv);
      toast.success("AI drafted your form — review & publish below.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI couldn't draft a form. Try a more detailed brief.");
    } finally {
      setAiBusy(false);
    }
  };

  // Pick the first numeric-eligible field as the indicator value source
  // (mirrors the Phase 1.5 auto-mapping rules). If the import has at
  // most one wired indicator we can confidently link the form; if
  // multiple rows wire different indicators we don't pick a winner
  // here — the user can edit the form afterwards.
  const buildPayload = (status) => {
    const fields = rows.map((r) => {
      const base = {
        id: `tmp-${Math.random().toString(36).slice(2, 8)}`,
        key: r.key,
        label: r.label,
        type: r.type,
        required: false,
      };
      if (["select", "radio", "checkbox"].includes(r.type)) base.options = ["Option 1", "Option 2"];
      if (r.type === "range") Object.assign(base, { min: 0, max: 100, step: 1 });
      return base;
    });
    const wired = rows.filter((r) => r.indicator_id);
    const uniqueIndicators = new Set(wired.map((r) => r.indicator_id));
    let indicator_id = null;
    let value_field_key = null;
    if (uniqueIndicators.size === 1) {
      indicator_id = wired[0].indicator_id;
      // First numeric-friendly field as value source.
      const valueField = fields.find((f) => ["text", "range"].includes(f.type) && f.key);
      value_field_key = valueField ? valueField.key : null;
    }
    return {
      name: formName.trim() || "Imported form",
      description: `Bulk-imported from CSV on ${new Date().toLocaleString()}.`,
      status,
      fields,
      indicator_id,
      value_field_key,
      period_field_key: null,
    };
  };

  const save = async (status) => {
    if (!formName.trim()) { toast.error("Give the form a name first"); return; }
    if (rows.length === 0) { toast.error("No fields to import"); return; }
    setSaving(true);
    try {
      const body = buildPayload(status);
      await api.post(`/programs/${pid}/forms`, body);
      toast.success(
        status === "published"
          ? `Form "${body.name}" published with ${body.fields.length} field${body.fields.length === 1 ? "" : "s"}`
          : `Saved "${body.name}" as draft (${body.fields.length} field${body.fields.length === 1 ? "" : "s"})`,
      );
      onImported && onImported();
      close();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not import form");
    } finally {
      setSaving(false);
    }
  };

  const unmatchedCount = rows.filter((r) => r.warning).length;
  const wiredCount = rows.filter((r) => r.indicator_id).length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="bulk-form-import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-600" />
            Bulk import form from CSV
          </DialogTitle>
          <DialogDescription>
            Paste a CSV of <span className="font-mono">Field Label, Type, Indicator Name</span> rows
            and Array Engine generates a ready-to-publish form.
          </DialogDescription>
        </DialogHeader>

        {step === "pick" && (
          <div className="space-y-5" data-testid="bulk-form-pick-step">
            {/* AI-suggest: paste a brief → Claude returns the 3-col CSV. */}
            <div className="border border-emerald-200 bg-emerald-50/40 rounded-sm p-5" data-testid="bulk-form-ai-panel">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="label-overline flex items-center gap-1.5">
                    <Wand2 className="w-3.5 h-3.5 text-emerald-600" /> AI suggest
                  </p>
                  <p className="text-sm text-slate-700 mt-2 leading-relaxed">
                    Tell Claude what you&apos;re measuring and it&apos;ll draft the full form
                    — including matched indicator names — for you to review.
                  </p>
                </div>
                <span className="shrink-0 text-[10px] uppercase tracking-wider bg-white border border-emerald-200 text-emerald-700 px-1.5 py-0.5 rounded-sm font-medium">
                  Claude
                </span>
              </div>
              <textarea
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                placeholder="e.g. We're tracking children enrolled, teachers trained, and water-points maintained across 12 villages. Field workers visit each site monthly and record attendance, photos, and GPS coordinates."
                className="mt-3 w-full h-24 bg-white border border-emerald-200 rounded-sm p-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                data-testid="bulk-form-ai-brief"
                disabled={aiBusy}
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-[11px] text-slate-500">
                  {brief.trim().length}/4000 characters · uses Emergent LLM key
                </p>
                <Button
                  onClick={askAi}
                  disabled={aiBusy || brief.trim().length < 10}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="bulk-form-ai-suggest-btn"
                >
                  {aiBusy ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Drafting…</>
                  ) : (
                    <><Wand2 className="w-3.5 h-3.5 mr-1.5" /> Generate form with AI</>
                  )}
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center" aria-hidden>
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-[10px] uppercase tracking-wider text-slate-400">
                  Or bring your own CSV
                </span>
              </div>
            </div>

            <div className="border border-dashed border-slate-300 bg-slate-50 rounded-sm p-5">
              <p className="label-overline">Step 1 · Get the template</p>
              <p className="text-sm text-slate-600 mt-2">
                Download a starter CSV with the exact three headers and 3 example rows.
              </p>
              <Button
                onClick={downloadTemplate}
                variant="outline"
                size="sm"
                className="mt-3"
                data-testid="bulk-form-download-template"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" /> Download template
              </Button>
            </div>

            <div className="border border-slate-200 rounded-sm p-5">
              <p className="label-overline">Step 2a · Upload a CSV file</p>
              <div className="mt-3 flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  data-testid="bulk-form-file-input"
                  onChange={(e) => onFile(e.target.files?.[0])}
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  size="sm"
                  data-testid="bulk-form-file-btn"
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> Choose file
                </Button>
                <span className="text-xs text-slate-500">.csv up to a few MB</span>
              </div>
            </div>

            <div className="border border-slate-200 rounded-sm p-5">
              <p className="label-overline">Step 2b · Or paste CSV directly</p>
              <textarea
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder={"Field Label,Type,Indicator Name\nChildren enrolled,Number,Children enrolled"}
                className="mt-2 w-full h-32 font-mono text-xs bg-white border border-slate-200 rounded-sm p-2 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                data-testid="bulk-form-paste-area"
              />
              <Button
                onClick={() => ingest(csvText)}
                disabled={!csvText.trim()}
                size="sm"
                className="mt-3 bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="bulk-form-parse-btn"
              >
                Parse CSV
              </Button>
            </div>
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-4" data-testid="bulk-form-preview-step">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setStep("pick")}
              className="-ml-2"
              data-testid="bulk-form-back-btn"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
            </Button>

            <div>
              <Label htmlFor="bulk-form-name" className="label-overline">Form name</Label>
              <Input
                id="bulk-form-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="mt-2"
                data-testid="bulk-form-name-input"
              />
            </div>

            <div className="flex items-center gap-3 text-xs">
              <span className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-800 px-2 py-1 rounded-sm" data-testid="bulk-form-summary-fields">
                <CheckCircle2 className="w-3 h-3" /> {rows.length} field{rows.length === 1 ? "" : "s"}
              </span>
              {wiredCount > 0 && (
                <span className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-800 px-2 py-1 rounded-sm" data-testid="bulk-form-summary-wired">
                  <Sparkles className="w-3 h-3" /> {wiredCount} matched to indicators
                </span>
              )}
              {unmatchedCount > 0 && (
                <span className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1 rounded-sm" data-testid="bulk-form-summary-warnings">
                  <AlertTriangle className="w-3 h-3" /> {unmatchedCount} unmatched
                </span>
              )}
            </div>

            <ul className="space-y-2 max-h-[40vh] overflow-y-auto pr-1" data-testid="bulk-form-preview-list">
              {rows.map((r, idx) => (
                <li
                  key={`${r.key}-${idx}`}
                  className={`border rounded-sm p-3 ${r.warning ? "border-amber-200 bg-amber-50/40" : "border-slate-200 bg-white"}`}
                  data-testid={`bulk-form-row-${idx}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-900">{r.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        type <span className="font-mono">{r.type}</span>
                        {r.type_raw && r.type_raw.toLowerCase() !== r.type && (
                          <span className="text-slate-400 ml-1">(from &ldquo;{r.type_raw}&rdquo;)</span>
                        )}
                        <span className="mx-1.5">·</span>
                        key <span className="font-mono">{r.key}</span>
                      </p>
                    </div>
                    {r.indicator_id ? (
                      <span className="shrink-0 inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-sm text-[10px] font-medium" data-testid={`bulk-form-row-matched-${idx}`}>
                        <Sparkles className="w-2.5 h-2.5" />
                        {r.indicator_code}
                      </span>
                    ) : r.indicator_name ? (
                      <span className="shrink-0 inline-flex items-center gap-1 bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-sm text-[10px] font-medium" data-testid={`bulk-form-row-warning-${idx}`}>
                        <AlertTriangle className="w-2.5 h-2.5" />
                        Not found
                      </span>
                    ) : null}
                  </div>
                  {r.warning && (
                    <p className="mt-2 text-xs text-amber-800">{r.warning}</p>
                  )}
                </li>
              ))}
            </ul>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <Button
                variant="ghost"
                onClick={close}
                disabled={saving}
                data-testid="bulk-form-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={() => save("draft")}
                disabled={saving}
                data-testid="bulk-form-save-draft-btn"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                Save draft
              </Button>
              <Button
                onClick={() => save("published")}
                disabled={saving}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="bulk-form-publish-btn"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                Publish form
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
